import { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MeetingEditor = ({ content, onChange, canEdit }) => {
  const [editorContent, setEditorContent] = useState(content || '');
  const [showToolbar, setShowToolbar] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    setEditorContent(content || '');
  }, [content]);

  const handleContentChange = (e) => {
    const newContent = e?.target?.value;
    setEditorContent(newContent);
    onChange(newContent);
  };

  const insertTemplate = (template) => {
    const templates = {
      agenda: `## Meeting Agenda\n\n1. Review of Previous Action Items\n2. Operational Performance Review\n3. Financial Summary\n4. Outstanding Issues\n5. Upcoming Shipments\n6. Action Items & Next Steps\n\n`,
      operational: `## Operational Performance\n\n### Volume Summary\n- Total Tonnage: [Enter tonnage]\n- Number of Shipments: [Enter count]\n- Average Vessel Size: [Enter size]\n\n### Key Metrics\n- On-time Delivery Rate: [Enter %]\n- Quality Compliance: [Enter %]\n- Cost Variance: [Enter %]\n\n`,
      financial: `## Financial Summary\n\n### Revenue\n- Gross Fees: $[Enter amount]\n- Reimbursable Costs: $[Enter amount]\n- Net Revenue: $[Enter amount]\n\n### Costs\n- Inspection Costs: $[Enter amount]\n- Lab Expenses: $[Enter amount]\n- Other Costs: $[Enter amount]\n\n`,
      issues: `## Outstanding Issues\n\n### Critical Issues\n1. [Issue description]\n   - Impact: [Enter impact]\n   - Owner: [Enter owner]\n   - Target Resolution: [Enter date]\n\n### Medium Priority\n1. [Issue description]\n\n`,
      forecast: `## Forecast & Planning\n\n### Next Month Projections\n- Expected Tonnage: [Enter tonnage]\n- Number of Shipments: [Enter count]\n- Estimated Revenue: $[Enter amount]\n\n### Key Considerations\n- [Enter consideration 1]\n- [Enter consideration 2]\n\n`
    };

    const textarea = editorRef?.current;
    const start = textarea?.selectionStart;
    const end = textarea?.selectionEnd;
    const newContent = editorContent?.substring(0, start) + templates?.[template] + editorContent?.substring(end);
    setEditorContent(newContent);
    onChange(newContent);
  };

  const formatText = (format) => {
    const textarea = editorRef?.current;
    const start = textarea?.selectionStart;
    const end = textarea?.selectionEnd;
    const selectedText = editorContent?.substring(start, end);

    if (!selectedText) return;

    let formattedText = selectedText;
    switch (format) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        break;
      case 'heading':
        formattedText = `## ${selectedText}`;
        break;
      case 'bullet':
        formattedText = `- ${selectedText}`;
        break;
      case 'number':
        formattedText = `1. ${selectedText}`;
        break;
      default:
        break;
    }

    const newContent = editorContent?.substring(0, start) + formattedText + editorContent?.substring(end);
    setEditorContent(newContent);
    onChange(newContent);
  };

  const insertTable = () => {
    const table = `\n| Header 1 | Header 2 | Header 3 |\n|----------|----------|----------|\n| Cell 1   | Cell 2   | Cell 3   |\n| Cell 4   | Cell 5   | Cell 6   |\n\n`;
    const textarea = editorRef?.current;
    const start = textarea?.selectionStart;
    const newContent = editorContent?.substring(0, start) + table + editorContent?.substring(start);
    setEditorContent(newContent);
    onChange(newContent);
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="border-b border-border p-3 bg-muted/30">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon name="FileText" size={20} className="text-primary" />
            <h3 className="font-semibold text-foreground">Meeting Notes</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName={showToolbar ? 'ChevronUp' : 'ChevronDown'}
            iconPosition="right"
            onClick={() => setShowToolbar(!showToolbar)}
          >
            {showToolbar ? 'Hide' : 'Show'} Toolbar
          </Button>
        </div>

        {showToolbar && canEdit && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1 p-1 bg-card rounded border border-border">
                <button
                  onClick={() => formatText('bold')}
                  className="p-1.5 rounded hover:bg-accent/10 text-muted-foreground hover:text-accent transition-colors"
                  title="Bold (Ctrl+B)"
                >
                  <Icon name="Bold" size={16} />
                </button>
                <button
                  onClick={() => formatText('italic')}
                  className="p-1.5 rounded hover:bg-accent/10 text-muted-foreground hover:text-accent transition-colors"
                  title="Italic (Ctrl+I)"
                >
                  <Icon name="Italic" size={16} />
                </button>
                <button
                  onClick={() => formatText('heading')}
                  className="p-1.5 rounded hover:bg-accent/10 text-muted-foreground hover:text-accent transition-colors"
                  title="Heading"
                >
                  <Icon name="Heading" size={16} />
                </button>
              </div>

              <div className="flex items-center gap-1 p-1 bg-card rounded border border-border">
                <button
                  onClick={() => formatText('bullet')}
                  className="p-1.5 rounded hover:bg-accent/10 text-muted-foreground hover:text-accent transition-colors"
                  title="Bullet List"
                >
                  <Icon name="List" size={16} />
                </button>
                <button
                  onClick={() => formatText('number')}
                  className="p-1.5 rounded hover:bg-accent/10 text-muted-foreground hover:text-accent transition-colors"
                  title="Numbered List"
                >
                  <Icon name="ListOrdered" size={16} />
                </button>
                <button
                  onClick={insertTable}
                  className="p-1.5 rounded hover:bg-accent/10 text-muted-foreground hover:text-accent transition-colors"
                  title="Insert Table"
                >
                  <Icon name="Table" size={16} />
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">Quick Templates:</span>
              <Button
                variant="outline"
                size="xs"
                onClick={() => insertTemplate('agenda')}
              >
                Agenda
              </Button>
              <Button
                variant="outline"
                size="xs"
                onClick={() => insertTemplate('operational')}
              >
                Operational
              </Button>
              <Button
                variant="outline"
                size="xs"
                onClick={() => insertTemplate('financial')}
              >
                Financial
              </Button>
              <Button
                variant="outline"
                size="xs"
                onClick={() => insertTemplate('issues')}
              >
                Issues
              </Button>
              <Button
                variant="outline"
                size="xs"
                onClick={() => insertTemplate('forecast')}
              >
                Forecast
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className="p-4">
        <textarea
          ref={editorRef}
          value={editorContent}
          onChange={handleContentChange}
          disabled={!canEdit}
          className="w-full min-h-[500px] p-4 bg-background border border-border rounded-lg text-foreground font-data text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-60 disabled:cursor-not-allowed"
          placeholder="Start typing your meeting notes here...\n\nTips:\n- Use ## for headings\n- Use **text** for bold\n- Use *text* for italic\n- Use - for bullet points\n- Use 1. for numbered lists\n- Use templates from toolbar for quick formatting"
        />
      </div>
      <div className="border-t border-border p-3 bg-muted/30">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-4">
            <span>{editorContent?.length} characters</span>
            <span>{editorContent?.split('\n')?.length} lines</span>
            <span>{editorContent?.split(/\s+/)?.filter(word => word?.length > 0)?.length} words</span>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <Icon name="Info" size={14} />
              <span>Ctrl+B: Bold | Ctrl+I: Italic | Ctrl+Shift+A: Add Action</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MeetingEditor;